import { Language } from '../enums/languages';

/**
 * What i18next resolves a key to when the locale holds no usable value for it.
 * Shared by the three SPA i18n configs so all of them answer the same way.
 *
 * `returnEmptyString: false` is the load-bearing one: locale parity only asks
 * whether a key is *present*, so an untranslated key ships as `""` and i18next
 * would hand that to the component — blank copy, or an `aria-label=""` with no
 * accessible name at all. Its cost is that an empty `en` value now resolves to
 * the key itself, which is why `check-translations.js` fails on one. #2199.
 */
export const I18N_MISSING_VALUE_OPTIONS = {
  fallbackLng: Language.EN,
  /** A `null` value resolves to the key, never to the string "null". */
  returnNull: false,
  /** An empty value is missing, not a translation — fall back to `en`. */
  returnEmptyString: false,
} as const;

type TranslationBundle = { [key: string]: unknown };

/**
 * A fan group's custom bundle is merged over the default one, so an empty
 * custom value would mask a real default and — now that empty means missing —
 * render the key itself. An empty override is no override, at any depth: the
 * `web` bundle nests a `preferences` block, and a block left with nothing in it
 * is dropped rather than spread over the default's copy. `check-translations.js`
 * cannot see these bundles — they are per-fan-group objects in S3, not repo
 * files. A *partial* nested override still replaces its whole block, which is
 * the shallow merge at the call site and not this function's to change.
 */
export const withoutEmptyValues = <T extends TranslationBundle>(bundle: T): T =>
  Object.fromEntries(
    Object.entries(bundle).flatMap(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const kept = withoutEmptyValues(value as TranslationBundle);
        return Object.keys(kept).length ? [[key, kept]] : [];
      }
      return typeof value === 'string' && value.trim() === '' ? [] : [[key, value]];
    })
  ) as T;
